import { useSeo } from "@/lib/seo";
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Image,
  Stack,
  Text,
  Textarea,
  Title,
} from "@repo/ui/core";
import { useForm } from "@repo/ui/form";
import { IconAlertCircle, IconCheck } from "@repo/ui/icons";
import { useState } from "react";
import styles from "@styles/site.module.css";

export default function Feedback() {
  useSeo("/mb/feedback");

  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const webhookUrl = "https://ctrl.null.autos/api/v1/feedback/mb";

  const form = useForm({
    initialValues: { message: "" },
    validate: {
      message: (value) => (value.trim().length === 0 ? "Message is required" : null),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setSending(true);
    setError(null);
    try {
      const res = await fetch(webhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ FEEDBACK_INPUT: values.message }),
      });
      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }
      setSuccess(true);
    } catch {
      setError("Your feedback could not be sent. Please try again in a moment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Box className={styles.pageHero} style={{ minHeight: "100dvh", borderBottom: "none" }}>
      <Container size="sm" className={styles.pageHeroInner}>
        <Stack align="center" mb="xl" gap="lg">
          <span className={styles.eyebrow}>Workshop 2026-01-29</span>
          <Image src="/null/mercedes-benz.png" alt="Mercedes-Benz" w={120} />
          <Title order={1} ta="center" className={styles.pageTitle}>
            Workshop Objectives
          </Title>
          <Text size="lg" c="dimmed" ta="center" maw={540} lh={1.7}>
            Help us tailor the workshop to your needs. Let us know what aspects of our Android
            Automotive CI/CD platform you&apos;d like to explore.
          </Text>
        </Stack>

        {success ? (
          <Card padding="xl" className={styles.card}>
            <Stack align="center" gap="md" py="xl">
              <span className={styles.iconChip} style={{ width: 56, height: 56 }}>
                <IconCheck size={28} />
              </span>
              <Title order={2} ta="center" fw={650}>
                Thank you
              </Title>
              <Text size="lg" c="dimmed" ta="center">
                We&apos;ll review your feedback and see you at the workshop.
              </Text>
            </Stack>
          </Card>
        ) : (
          <Card padding="xl" className={styles.card}>
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack gap="lg">
                {error && (
                  <Alert variant="light" color="red" icon={<IconAlertCircle size={18} />} role="alert">
                    {error}
                  </Alert>
                )}
                <Textarea
                  label="Message"
                  placeholder="What would make this workshop most valuable for you and your team?"
                  withAsterisk
                  rows={12}
                  autoFocus
                  disabled={sending}
                  {...form.getInputProps("message")}
                />
                <Button
                  type="submit"
                  size="lg"
                  loading={sending}
                  disabled={sending}
                  style={{
                    background: "var(--na-text)",
                    color: "var(--na-bg)",
                    borderRadius: "var(--na-r-sm)",
                    fontWeight: 550,
                  }}
                >
                  Submit Feedback
                </Button>
              </Stack>
            </form>
          </Card>
        )}
      </Container>
    </Box>
  );
}
